import api from "@/services/api";

const BASE_URL = "/budget-monitor";

//#region state
const state = {
  pos: [],
  posTotal: 0,
  jobs: [],
  jobsTotal: 0,
  poDetail: null,
  jobDetail: null,
  lines: [],
  history: [],
  runs: [],
  setting: null,
  recipients: {},
  overrides: [],
};
//#endregion

//#region getters
const getters = {
  lastRun: state => (state.runs && state.runs.length ? state.runs[0] : null),
};
//#endregion

const call = async (fn, commit, mutation, context) => {
  try {
    const response = await fn();
    if (mutation) {
      context.commit(mutation, response.data);
    }
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

//#region actions
const actions = {
  fetchPos(context, params = {}) {
    return call(() => api.get(`${BASE_URL}/pos`, params), true, "SET_POS", context);
  },
  fetchPoDetail(context, crmPoId) {
    return call(() => api.get(`${BASE_URL}/pos/${crmPoId}`), true, "SET_PO_DETAIL", context);
  },
  fetchPoLines(context, { crmPoId, bucket, jobId, groupBy }) {
    const params = {};
    if (bucket) params.bucket = bucket;
    if (jobId !== null && jobId !== undefined && jobId !== "") params.jobId = jobId;
    if (groupBy) params.groupBy = groupBy;
    return call(
      () => api.get(`${BASE_URL}/pos/${crmPoId}/lines`, params),
      true,
      "SET_LINES",
      context
    );
  },
  fetchPoHistory(context, { crmPoId, days = 365 }) {
    return call(
      () => api.get(`${BASE_URL}/pos/${crmPoId}/history`, { days }),
      true,
      "SET_HISTORY",
      context
    );
  },
  fetchJobs(context, params = {}) {
    return call(() => api.get(`${BASE_URL}/jobs`, params), true, "SET_JOBS", context);
  },
  fetchJobDetail(context, jobId) {
    return call(() => api.get(`${BASE_URL}/jobs/${jobId}`), true, "SET_JOB_DETAIL", context);
  },
  fetchJobLines(context, { jobId, bucket, crmPoId, groupBy }) {
    const params = {};
    if (bucket) params.bucket = bucket;
    if (crmPoId !== null && crmPoId !== undefined && crmPoId !== "") params.crmPoId = crmPoId;
    if (groupBy) params.groupBy = groupBy;
    return call(
      () => api.get(`${BASE_URL}/jobs/${jobId}/lines`, params),
      true,
      "SET_LINES",
      context
    );
  },
  fetchNoJobLines(context, bucket) {
    const params = bucket ? { bucket } : {};
    return call(() => api.get(`${BASE_URL}/no-job/lines`, params), false, null, context);
  },
  recomputePo(context, crmPoId) {
    return call(() => api.post(`${BASE_URL}/pos/${crmPoId}/recompute`), false, null, context);
  },
  recomputeJob(context, jobId) {
    return call(() => api.post(`${BASE_URL}/jobs/${jobId}/recompute`), false, null, context);
  },
  recomputeAll(context) {
    return call(() => api.post(`${BASE_URL}/recompute`), false, null, context);
  },
  fetchRuns(context) {
    return call(() => api.get(`${BASE_URL}/runs`), true, "SET_RUNS", context);
  },
  fetchSetting(context) {
    return call(() => api.get(`${BASE_URL}/settings`), true, "SET_SETTING", context);
  },
  saveSetting(context, dto) {
    return call(() => api.put(`${BASE_URL}/settings`, dto), true, "SET_SETTING", context);
  },
  fetchRecipients(context) {
    return call(() => api.get(`${BASE_URL}/recipients`), true, "SET_RECIPIENTS", context);
  },
  saveRecipients(context, { notificationType, recipients }) {
    return call(
      () => api.put(`${BASE_URL}/recipients`, { notificationType, recipients }),
      false,
      null,
      context
    );
  },
  sendTestEmail(context, to) {
    return call(() => api.post(`${BASE_URL}/recipients/test-email`, { to }), false, null, context);
  },
  fetchOverrides(context) {
    return call(() => api.get(`${BASE_URL}/margin-overrides`), true, "SET_OVERRIDES", context);
  },
  saveOverride(context, { crmPoId, marginPercent, reason }) {
    return call(
      () => api.put(`${BASE_URL}/margin-overrides/${crmPoId}`, { crmPoId, marginPercent, reason }),
      false,
      null,
      context
    );
  },
  deleteOverride(context, crmPoId) {
    return call(() => api.deleted(`${BASE_URL}/margin-overrides/${crmPoId}`), false, null, context);
  },
  exportPo(context, crmPoId) {
    return api.get(`${BASE_URL}/pos/${crmPoId}/export`, {}, "blob");
  },
};
//#endregion

//#region mutations
const mutations = {
  SET_POS(state, page) {
    state.pos = (page && page.items) || [];
    state.posTotal = (page && page.total) || 0;
  },
  SET_JOBS(state, rows) {
    state.jobs = Array.isArray(rows) ? rows : (rows && rows.items) || [];
    state.jobsTotal = state.jobs.length;
  },
  SET_PO_DETAIL(state, detail) {
    state.poDetail = detail || null;
  },
  SET_JOB_DETAIL(state, detail) {
    state.jobDetail = detail || null;
  },
  SET_LINES(state, lines) {
    state.lines = lines || [];
  },
  SET_HISTORY(state, history) {
    state.history = history || [];
  },
  SET_RUNS(state, runs) {
    state.runs = runs || [];
  },
  SET_SETTING(state, setting) {
    state.setting = setting || null;
  },
  SET_RECIPIENTS(state, list) {
    const map = {};
    (list || []).forEach(r => {
      map[r.notificationType] = r.recipients || [];
    });
    state.recipients = map;
  },
  SET_OVERRIDES(state, rows) {
    state.overrides = rows || [];
  },
};
//#endregion

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
